import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { API_URLS } from '../common/config';
import type { ListingsAPIErrorResponseBody } from '../common/types/ListingsAPIErrorResponseBody';
import type {
  RecommendationsRequestBody,
  RecommendationsRequestHeaders,
} from './types/RecommendationsRequestBody';
import type { RecommendationsResponseBody } from './types/RecommendationsResponseBody';
import type { RecommendationsServingConfig } from './types/RecommendationsServingConfig';

const DEFAULT_TIMEOUT = 5000;

export type FetchRecommendationsDataArgs = {
  body: RecommendationsRequestBody;
  clientSide?: boolean;
  env: 'qa' | 'prod';
  servingConfig?: RecommendationsServingConfig;
  timeout?: number;
  // Full request URL, overriding the built-in one for `env`. Lets a consumer
  // drive the URL from its own config rather than this package's defaults.
  url?: string;
} & Pick<RecommendationsRequestHeaders, 'userId' | 'visitorId' | 'validateOnly'>;

export const fetchRecommendationsData = async ({
  body,
  clientSide = false,
  env,
  servingConfig,
  timeout = DEFAULT_TIMEOUT,
  url,
  userId,
  validateOnly = false,
  visitorId,
}: FetchRecommendationsDataArgs): Promise<
  RecommendationsResponseBody | ListingsAPIErrorResponseBody
> => {
  const envConfig = API_URLS.recommendations[env];
  const requestUrl = url ?? (clientSide ? envConfig.clientSide : envConfig.serverSide);
  if (!visitorId) {
    throw new Error('visitorId must be specified');
  }

  const apiResponse = await fetchJSONWithTimeout(requestUrl, {
    method: 'POST',
    timeout,
    headers: {
      ...(userId && { 'noths-user-id': userId }),
      'noths-visitor-id': visitorId,
      'Content-Type': 'application/json',
      ...(servingConfig && { 'noths-serving-config': servingConfig }),
      'validate-only': validateOnly.toString(),
    },
    body: body as unknown as Record<string, unknown>,
  });

  const data = await apiResponse.json();

  if (apiResponse.ok) {
    return data as RecommendationsResponseBody;
  }

  if (data.message) {
    return data as ListingsAPIErrorResponseBody;
  }

  return data;
};
